import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  useTheme,
  Paper
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import type { ImportPreview, ImportResult } from '../../lib/utils/csvUtils';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import HelpTooltipTrigger from '../common/HelpTooltipTrigger';
import { quickFillActionTooltips } from '../../config/actionHelpCatalog';
import { OperationProgressPanel, type OperationProgressModel } from '../progress';

export interface CSVImportPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<ImportResult>;
  onPreview: (file: File) => Promise<ImportPreview>;
}

const CSVImportPreviewDialog: React.FC<CSVImportPreviewDialogProps> = ({
  open,
  onClose,
  onImport,
  onPreview
}) => {
  const theme = useTheme();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setPreview(null);
    setImportResult(null);
    setError(null);
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setError(null);
    setImportResult(null);
    setLoading(true);

    try {
      const previewData = await onPreview(file);
      setPreview(previewData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview file');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [onPreview]);

  // Handle import
  const handleImport = useCallback(async () => {
    if (!selectedFile) return;

    setImporting(true);
    setError(null);

    try {
      const result = await onImport(selectedFile);
      setImportResult(result);

      if (result.success) {
        // Auto-close after successful import (with delay for user to see result)
        setTimeout(() => {
          onClose();
          resetState();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import file');
    } finally {
      setImporting(false);
    }
  }, [selectedFile, onImport, onClose, resetState]);

  // Handle close
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const activeProgressModel: OperationProgressModel | null = loading
    ? {
        title: 'Previewing CSV',
        message: 'Analyzing CSV file',
        detail: selectedFile
          ? `Reading ${selectedFile.name} before showing the import preview.`
          : 'Reading selected rows before showing the import preview.',
        status: 'running',
        phase: 'Analyzing file',
        percent: null,
        locked: true,
      }
    : importing
      ? {
          title: 'Importing CSV',
          message: `Applying ${preview?.matchedRows ?? 0} matched row${preview?.matchedRows === 1 ? '' : 's'}`,
          detail: 'Updating the current Quick Fill table with previewed CSV values.',
          status: 'running',
          phase: 'Importing rows',
          processed: 0,
          total: preview?.matchedRows ?? null,
          percent: null,
          locked: true,
        }
      : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="csv-import-dialog-title"
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle id="csv-import-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadFileIcon color="primary" />
          <Typography variant="h6">Import CSV</Typography>
          <HelpTooltipTrigger
            label="Import CSV"
            help={quickFillActionTooltips.importCsvDialog}
            placement="bottom-start"
          />
        </Box>
        <IconButton onClick={handleClose} aria-label="Close CSV import dialog" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* File Input */}
        <Box sx={{ mb: 3 }}>
          <ActionHelpTooltip help={quickFillActionTooltips.importCsvSelectFile}>
            <Button
              variant="outlined"
              component="label"
              aria-label="Select CSV file to import"
              startIcon={<UploadFileIcon />}
              disabled={importing}
            >
              Select CSV File
              <input
                type="file"
                hidden
                accept=".csv,.txt"
                onChange={handleFileSelect}
              />
            </Button>
          </ActionHelpTooltip>

          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
        </Box>

        {/* Loading */}
        {activeProgressModel && (
          <Box sx={{ mb: 2 }}>
            <OperationProgressPanel
              model={activeProgressModel}
              compact
              testId={loading ? 'csv-import-preview-progress' : 'csv-import-apply-progress'}
            />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Import Result */}
        {importResult && (
          <Alert
            severity={importResult.success ? 'success' : 'warning'}
            icon={importResult.success ? <CheckCircleIcon /> : <WarningIcon />}
            sx={{ mb: 2 }}
          >
            {importResult.success
              ? `Successfully updated ${importResult.updatedCount} rows.${importResult.skippedCount > 0 ? ` ${importResult.skippedCount} rows skipped (not found in current data).` : ''}`
              : `Import completed with issues: ${importResult.errors.join(', ')}`
            }
          </Alert>
        )}

        {/* Preview */}
        {preview && !importResult && (
          <>
            {/* Warnings */}
            {preview.warnings.map((warning, index) => (
              <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                {warning}
              </Alert>
            ))}

            {/* Stats */}
            <Paper sx={{ p: 2, mb: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Import summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
            <Typography variant="body2" color="text.secondary">Total rows</Typography>
                  <Typography variant="h6">{preview.totalRows}</Typography>
                </Box>
                <Box>
            <Typography variant="body2" color="text.secondary">Will update</Typography>
                  <Typography variant="h6" color="success.main">{preview.matchedRows}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Skipped (new)</Typography>
                  <Typography variant="h6" color="warning.main">{preview.newRows}</Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Note: New rows are skipped because data comes from Revit. Only existing rows will be updated.
              </Typography>
            </Paper>

            {/* Preview Table */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview (first 10 rows)
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {preview.headers.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {preview.totalRows > 10 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                ... and {preview.totalRows - 10} more rows
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <ActionHelpTooltip help={quickFillActionTooltips.importCsvApply}>
          <Button
            onClick={handleImport}
            aria-label="Import previewed CSV rows"
            variant="contained"
            color="primary"
            disabled={!preview || importing || !!importResult || preview.matchedRows === 0}
            startIcon={importing ? undefined : <UploadFileIcon />}
          >
            {importing ? 'Importing…' : `Import ${preview?.matchedRows || 0} rows`}
          </Button>
        </ActionHelpTooltip>
      </DialogActions>
    </Dialog>
  );
};

export default CSVImportPreviewDialog;
